/**
 * OmniMind Chat Component
 * The Ask screen: a grounded chat stream on the left, and an evidence rail on
 * the right listing the sources an answer can draw on.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { ThinkingIndicator } from './ThinkingIndicator';
import { AppShell } from '../shell/AppShell';
import { Icon, ICONS } from '../shell/icons';
import { useChat } from '../../hooks/useChat';
import { useAuth } from '../../hooks/useAuth';
import { useDatasources } from '../../hooks/useDatasources';
import { usePlatformSelection } from '../../hooks/usePlatformSelection';
import { PLATFORM_COLORS, Platform } from '../../types/platform.types';
import { formatRelativeTime } from '../../utils/formatTime';
import { formatRole } from '../../utils/formatRole';
import { ELAMA_SOURCES } from '../../demo/live';
import {
  IntelligenceReportModal,
  briefingSeenToday,
} from '../report/IntelligenceReportModal';
import { isFallbackAnswer, matchDemoAnswer } from '../../demo/answers';

const STARTER_KINDS = ['Diagnose', 'Decide', 'Forecast', 'Discover'];

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function platformColor(platform: Platform): string {
  return PLATFORM_COLORS[platform.id]?.primary || '#2354F0';
}

export const OmniMindChat: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { messages, loading, error, sendMessage } = useChat();

  const { platforms, datasources, loading: loadingPlatforms } = useDatasources();
  const { selectedPlatforms, isCollapsed, selectPlatform, togglePanel } = usePlatformSelection();

  const [elama, setElama] = useState(ELAMA_SOURCES.map((s) => s.on));
  // the daily briefing greets you once a day, as on the live product
  const [briefing, setBriefing] = useState(() => !briefingSeenToday());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const [showThinking, setShowThinking] = useState(false);
  // true while the reader is at the bottom; a reader who has scrolled up to
  // re-read an earlier answer should not be yanked down by a new message
  const stickToBottom = useRef(true);
  const [showJump, setShowJump] = useState(false);

  const atBottom = (el: HTMLElement, slack = 80) =>
    el.scrollHeight - el.scrollTop - el.clientHeight <= slack;

  const onStreamScroll = () => {
    const el = streamRef.current;
    if (!el) return;
    stickToBottom.current = atBottom(el);
    setShowJump(!stickToBottom.current && el.scrollHeight - el.clientHeight > 200);
  };

  useEffect(() => {
    const el = streamRef.current;
    if (!el || !stickToBottom.current) return;
    // scroll the stream itself rather than scrollIntoView, which can scroll
    // the page and the sidebar along with it
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, showThinking]);

  /** Jump to the newest message and resume following it. */
  const jumpToLatest = () => {
    const el = streamRef.current;
    if (!el) return;
    stickToBottom.current = true;
    setShowJump(false);
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  const handleSendMessage = async (content: string, attachments?: File[], voiceBlob?: Blob) => {
    // asking something is an explicit request to see the answer
    stickToBottom.current = true;
    try {
      setShowThinking(true);
      await sendMessage({ content, attachments, voiceBlob, selectedPlatforms });
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setShowThinking(false);
    }
  };

  const liveCount = platforms.filter((p) => p.id !== 'all' && p.available).length;
  const roleLabel = formatRole(user?.role);
  const plural = liveCount === 1 ? '' : 's';

  /* ---------- sidebar: which sources a question runs against ---------- */
  const sidebarExtra = (
    <>
      <div className="nav-lbl">Ask across</div>
      {loadingPlatforms ? (
        <div className="dim" style={{ padding: '10px' }}>
          Loading sources…
        </div>
      ) : platforms.length === 0 ? (
        <div className="note" style={{ margin: '4px 6px' }}>
          No sources connected yet. <b>Add one</b> to start asking grounded questions.
        </div>
      ) : (
        platforms.map((platform) => {
          const on = selectedPlatforms.includes(platform.id);
          return (
            <button
              key={platform.id}
              onClick={() => selectPlatform(platform.id)}
              className={`nav sub${on ? ' act' : ''}`}
              disabled={!platform.available}
              title={platform.available ? platform.displayName : 'Not configured'}
              style={!platform.available ? { opacity: 0.45 } : undefined}
            >
              <span className="dotk" style={{ background: platformColor(platform) }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {platform.displayName}
              </span>
              {platform.connectionStatus === 'syncing' && <span className="nb am">sync</span>}
              {platform.connectionStatus === 'error' && <span className="nb rd">!</span>}
            </button>
          );
        })
      )}

      <div className="nav-lbl">eLama sources</div>
      {ELAMA_SOURCES.map((src, i) => (
        <div className="srcrow" key={src.id}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="n">{src.name}</div>
            <div className="b">{src.blurb}</div>
          </div>
          <button
            className={`toggle${elama[i] ? ' on' : ''}`}
            onClick={() => setElama(elama.map((v, j) => (j === i ? !v : v)))}
            aria-pressed={elama[i]}
            title={elama[i] ? 'Switch off' : 'Switch on'}
          >
            <i />
          </button>
        </div>
      ))}
    </>
  );

  /* ---------- top bar ---------- */
  const actions = (
    <>
      <span className="chip2">
        <i style={{ background: liveCount ? 'var(--green)' : 'var(--muted-2)' }} />
        {liveCount} source{plural} live
      </span>
      <span className="chip2">
        <Icon d={ICONS.lock} />
        {roleLabel}
      </span>
      <button
        className="icb"
        onClick={() => navigate('/settings/datasources')}
        title="Data source settings"
      >
        <Icon d={ICONS.gear} />
      </button>
    </>
  );

  return (
    <>
      {briefing && <IntelligenceReportModal onClose={() => setBriefing(false)} />}
      <AppShell
      title="Ask"
      sub={
        liveCount > 0
          ? `Grounded in ${liveCount} connected source${plural}`
          : 'No sources connected yet'
      }
      actions={actions}
      sidebarExtra={sidebarExtra}
      sourceCount={datasources.length}
      body="flush"
      collapsed={isCollapsed}
      onToggleSidebar={togglePanel}
    >
      <div className="chatwrap">
        <div className="chatcol">
          <div className="stream custom-scrollbar" ref={streamRef} onScroll={onStreamScroll}>
            <div className="stream-in">
              {messages.length === 0 && (
                <>
                  <div className="hero">
                    <div className="orb">
                      <i />
                    </div>
                    <h2>
                      {greeting()}, {user?.fullName?.split(' ')[0] || 'there'}
                    </h2>
                    <p>
                      Ask anything about the business. Every answer is grounded in the sources you
                      have connected.
                    </p>
                  </div>
                  <div className="starters">
                    {getExampleQueries(user?.role || 'viewer').map((query, index) => (
                      <button
                        key={index}
                        className="starter"
                        onClick={() => handleSendMessage(query)}
                      >
                        <div className="k">{STARTER_KINDS[index % STARTER_KINDS.length]}</div>
                        <div className="q">{query}</div>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {messages.map((message, i) => {
                // Without a model key the backend replies with a fallback
                // sentence; show a worked example in its place, tagged as demo.
                const demo =
                  message.role === 'assistant' && isFallbackAnswer(message.content)
                    ? matchDemoAnswer(
                        [...messages.slice(0, i)].reverse().find((m) => m.role === 'user')
                          ?.content || ''
                      )
                    : undefined;

                return (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    demo={demo}
                    onFollowUp={handleSendMessage}
                  />
                );
              })}

              {showThinking && <ThinkingIndicator />}

              {error && (
                <div className="errbox" style={{ margin: '12px 0' }}>
                  <b>Could not answer that.</b> {error}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {showJump && (
            <button className="jump" onClick={jumpToLatest}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
              Latest
            </button>
          )}

          <div className="composer">
            <div className="composer-in">
              <ChatInput
                onSend={handleSendMessage}
                disabled={loading}
                placeholder="Ask about your business, or describe a decision you need to make…"
              />
              <div className="cmeta">
                <span className="chip2">
                  <i style={{ background: liveCount ? 'var(--green)' : 'var(--muted-2)' }} />
                  Grounded in {liveCount} source{plural}
                </span>
                <span className="chip2">
                  <Icon d={ICONS.lock} />
                  Your permissions: {roleLabel}
                </span>
                <span className="chip2">Read-only · no actions without approval</span>
                <span className="dim" style={{ fontSize: 11, marginLeft: 'auto' }}>
                  OmniMind can make mistakes. Check important info.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- evidence rail ---------- */}
        <div className="evid custom-scrollbar">
          <h5>Sources in scope</h5>
          {loadingPlatforms ? (
            <div className="note">Loading connected sources…</div>
          ) : platforms.length === 0 ? (
            <div className="note">
              Nothing connected yet. Add a datasource and OmniMind will answer from it.
            </div>
          ) : (
            platforms.map((platform) => {
              const on = selectedPlatforms.includes(platform.id);
              return (
                <button
                  key={platform.id}
                  className={`src click${on ? ' on' : ''}`}
                  onClick={() => platform.available && selectPlatform(platform.id)}
                  disabled={!platform.available}
                >
                  <span className="lg" style={{ background: platformColor(platform) }}>
                    {platform.logo ? (
                      <img src={platform.logo} alt="" style={{ width: 16, height: 16 }} />
                    ) : (
                      platform.displayName.slice(0, 2).toUpperCase()
                    )}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="t">{platform.displayName}</div>
                    <div className="d">
                      {!platform.available
                        ? 'Not configured'
                        : platform.connectionName || platform.name}
                    </div>
                  </div>
                  <span className="fr">
                    {platform.lastSyncAt ? formatRelativeTime(platform.lastSyncAt) : '—'}
                  </span>
                </button>
              );
            })
          )}

          <h5 style={{ marginTop: 16 }}>How answers are built</h5>
          <div className="src">
            <span className="lg" style={{ background: '#6D5AE6' }}>
              <Icon d={ICONS.db} className="w-3.5 h-3.5" />
            </span>
            <div style={{ flex: 1 }}>
              <div className="t">Retrieval before reasoning</div>
              <div className="d">
                The selected sources are queried first; the model reasons only over what came back.
              </div>
            </div>
          </div>
          <div className="src">
            <span className="lg" style={{ background: '#12B981' }}>
              <Icon d={ICONS.lock} className="w-3.5 h-3.5" />
            </span>
            <div style={{ flex: 1 }}>
              <div className="t">Your permissions apply</div>
              <div className="d">
                Queries run with your role, inherited from each connected system.
              </div>
            </div>
          </div>

          <h5 style={{ marginTop: 16 }}>Actions</h5>
          <button
            className="btn btn-g btn-s"
            style={{ width: '100%' }}
            onClick={() => navigate('/settings/datasources')}
          >
            Manage data sources
          </button>
        </div>
      </div>
    </AppShell>
    </>
  );
};

/**
 * Get example queries based on user role
 */
function getExampleQueries(role: string): string[] {
  const queries: Record<string, string[]> = {
    ceo: [
      'What are our top 3 revenue drivers this quarter?',
      'Show me customer churn trends over the last 6 months',
      'Compare department performance across regions',
      'What are the key risks in our current pipeline?',
    ],
    cfo: [
      'What is our cash flow projection for next quarter?',
      'Show me expense trends by department',
      'Compare actual vs budgeted spending',
      'What are our largest vendor payments this month?',
    ],
    sales_director: [
      'Show me top performing sales reps this month',
      'What deals are at risk of closing late?',
      'Compare sales performance across regions',
      'What is our average deal size trend?',
    ],
    hr_director: [
      'What is our current employee turnover rate?',
      'Show me hiring progress by department',
      'What are the top reasons for employee exits?',
      'Compare compensation across departments',
    ],
    default: [
      'What insights can you provide from our data?',
      'Show me recent trends in our organization',
      'Help me understand our current performance',
      'What should I know about this week?',
    ],
  };

  return queries[role] || queries.default;
}
